import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { authState, authActions } from '../../stores/authStore';
import { api } from '../../utils/api';
import { PasswordField } from '../../components/PasswordField';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { PageContainer } from '../../components/layout/PageContainer';
import { useToast } from '../../components/ui/Toast';
import { PhoneInput, isValidPhoneInputValue } from '../../components/ui/PhoneInput';
import { TotpSetupModal } from '../../components/auth/TotpSetupModal';
import { PasskeysCard } from '../../components/auth/PasskeysCard';
import { formatDateTime } from '../../utils/dateUtils';

export const Route = createFileRoute('/profile/')({
  component: MyProfilePage,
});

function MyProfilePage() {
  const { user } = useSnapshot(authState);
  const { showToast, ToastContainer } = useToast();

  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber ?? '');
  const [saving, setSaving] = useState(false);

  if (!user) {
    return (
      <div className="flex flex-1 overflow-auto">
        <NavigationSidebar />
        <PageContainer>
          <div className="p-6">
            <p className="text-gray-500">Not signed in.</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  const dirty = phoneNumber.trim() !== (user.phoneNumber ?? '');
  const phoneValid = isValidPhoneInputValue(phoneNumber.trim());

  async function handleSavePhone(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      await api.put('/auth/me/phone', { phoneNumber });
      // Refresh the auth state so the menu / future pages see the new value.
      await authActions.refreshUser();
      showToast(
        phoneNumber.trim().length === 0
          ? 'Phone number cleared'
          : 'Phone number saved',
        'success'
      );
    } catch (err) {
      showToast(`Failed to save phone: ${(err as Error).message}`, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-1 overflow-auto">
      <NavigationSidebar />
      <PageContainer>
        <div className="p-6 max-w-3xl space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <p className="text-gray-500 text-sm mt-1">
              Manage the contact details on your account. Used to deliver sign-in codes
              and account notifications.
            </p>
          </div>

          {user.mustChangePassword && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-amber-800 flex-1">
                <p className="font-medium">Password change required</p>
                <p className="mt-1">
                  An administrator has flagged your account to update its password.
                  Use the link below to set a new one — you'll be signed out of all devices once you do.
                </p>
                <Link
                  to="/forgot-password"
                  className="inline-block mt-2 text-amber-700 underline font-medium"
                >
                  Send me a reset email
                </Link>
              </div>
            </div>
          )}

          {/* Read-only identity */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Name</dt>
                <dd className="text-gray-900 font-medium mt-0.5">{user.firstName} {user.lastName}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Email</dt>
                <dd className="text-gray-900 font-medium mt-0.5">{user.email}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Company</dt>
                <dd className="text-gray-900 font-medium mt-0.5">{user.companyName ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Role</dt>
                <dd className="text-gray-900 font-medium mt-0.5 capitalize">
                  {user.role.replace('_', ' ')}
                </dd>
              </div>
            </dl>
          </div>

          {/* Phone number — used for SMS sign-in codes once enhanced auth is enabled. */}
          <form onSubmit={handleSavePhone} className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Mobile phone number</h2>
              <p className="text-sm text-gray-500 mt-1">
                Used to deliver sign-in verification codes by SMS once enhanced authentication is enabled.
                Use international format (e.g. <span className="font-mono">+44 7700 900123</span>) so the code reaches you reliably.
              </p>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone number
              </label>
              <PhoneInput
                id="phone"
                value={phoneNumber}
                onChange={setPhoneNumber}
                placeholder="7700 900123"
                invalid={!phoneValid}
                aria-describedby="phone-status"
              />
              <div id="phone-status" className="mt-2 flex items-center gap-2 text-xs">
                {!phoneValid && phoneNumber.length > 0 ? (
                  <span className="text-red-600">
                    Enter a valid international phone number.
                  </span>
                ) : user.phoneNumber ? (
                  user.phoneNumberVerified ? (
                    <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                      Not yet verified
                    </span>
                  )
                ) : (
                  <span className="text-gray-500">No phone number on file.</span>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!dirty || saving || !phoneValid}
                className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
                style={{ background: 'var(--color-accent)' }}
              >
                {saving ? 'Saving…' : phoneNumber.trim().length === 0 ? 'Clear phone number' : 'Save phone number'}
              </button>
            </div>
          </form>

          <TotpCard showToast={showToast} />

          <PasskeysCard showToast={showToast} />
        </div>
        <ToastContainer />
      </PageContainer>
    </div>
  );
}

// ─── TOTP / Authenticator-app card (Playbook PR 8) ─────────────────────
//
// Shows current enrolment state and offers Set Up / Disable / Regenerate
// Recovery Codes flows. The 3-step setup wizard is in components/auth/
// TotpSetupModal.tsx.

interface TotpStatus {
  enabled: boolean;
  enrolledAt: string | null;
  lastUsedAt: string | null;
  recoveryCodesRemaining: number;
}

function TotpCard({ showToast }: { showToast: (msg: string, t?: 'success' | 'error' | 'info') => void }) {
  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [error, setError] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);

  const reload = useCallback(async () => {
    try {
      const s = await authActions.getTotpStatus();
      setStatus(s);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Authenticator app</h2>
          <p className="text-sm text-gray-500 mt-1">
            Use Google Authenticator, 1Password, Authy, or any standards-compatible app to generate
            sign-in codes — no SMS required, works offline.
          </p>
        </div>
        {status && (
          status.enabled ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Enabled
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              Not enabled
            </span>
          )
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-3">{error}</div>
      )}

      {status && status.enabled && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
          <div>
            <dt className="text-gray-500">Enrolled</dt>
            <dd className="text-gray-900 mt-0.5">
              {status.enrolledAt ? formatDateTime(status.enrolledAt) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Last used</dt>
            <dd className="text-gray-900 mt-0.5">
              {status.lastUsedAt ? formatDateTime(status.lastUsedAt) : 'Never'}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-gray-500">Recovery codes remaining</dt>
            <dd
              className={`mt-0.5 font-mono ${status.recoveryCodesRemaining <= 2
                ? 'text-red-700'
                : status.recoveryCodesRemaining <= 4
                  ? 'text-amber-700'
                  : 'text-gray-900'}`}
            >
              {status.recoveryCodesRemaining} / 10
              {status.recoveryCodesRemaining <= 2 && (
                <span className="ml-2 text-xs font-sans font-normal">— consider regenerating</span>
              )}
            </dd>
          </div>
        </dl>
      )}

      <div className="flex flex-wrap gap-2">
        {status && !status.enabled && (
          <button
            onClick={() => setShowSetup(true)}
            className="px-4 py-2 text-sm text-white font-medium rounded-lg"
            style={{ background: 'var(--color-accent)' }}
          >
            Set up authenticator
          </button>
        )}
        {status && status.enabled && (
          <>
            <button
              onClick={() => setShowRegen(true)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Regenerate recovery codes
            </button>
            <button
              onClick={() => setShowDisable(true)}
              className="px-4 py-2 text-sm border border-red-200 text-red-700 rounded-lg hover:bg-red-50"
            >
              Disable authenticator
            </button>
          </>
        )}
      </div>

      {showSetup && (
        <TotpSetupModal
          onClose={(didEnrol) => {
            setShowSetup(false);
            if (didEnrol) {
              showToast('Authenticator app enabled', 'success');
              reload();
            }
          }}
        />
      )}

      {showDisable && (
        <DisableTotpModal
          onClose={(didDisable) => {
            setShowDisable(false);
            if (didDisable) {
              showToast('Authenticator app disabled', 'success');
              reload();
            }
          }}
        />
      )}

      {showRegen && (
        <RegenerateRecoveryCodesModal
          onClose={(codes) => {
            setShowRegen(false);
            if (codes) {
              setNewRecoveryCodes(codes);
              showToast('Recovery codes regenerated', 'success');
              reload();
            }
          }}
        />
      )}

      {newRecoveryCodes && (
        <NewRecoveryCodesModal
          codes={newRecoveryCodes}
          onClose={() => setNewRecoveryCodes(null)}
        />
      )}
    </div>
  );
}

function DisableTotpModal({ onClose }: { onClose: (didDisable: boolean) => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await authActions.disableTotp(password);
      onClose(true);
    } catch (err) {
      setError((err as Error).message || 'Failed to disable');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Disable authenticator app</h3>
        <p className="text-sm text-gray-600 mb-4">
          You'll fall back to SMS for sign-in (or whichever 2FA method you set up later). Confirm with
          your password.
        </p>
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-3">{error}</div>}
        <PasswordField
          autoFocus
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Current password"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
        />
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            disabled={busy}
            onClick={() => onClose(false)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || password.length === 0}
            className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50 bg-red-600 hover:bg-red-700"
          >
            {busy ? 'Disabling…' : 'Disable'}
          </button>
        </div>
      </form>
    </div>
  );
}

function RegenerateRecoveryCodesModal({ onClose }: { onClose: (codes: string[] | null) => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await authActions.regenerateTotpRecoveryCodes(code);
      onClose(r.recoveryCodes);
    } catch (err) {
      setError((err as Error).message || 'Failed to regenerate');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Regenerate recovery codes</h3>
        <p className="text-sm text-gray-600 mb-4">
          This will <strong>invalidate any existing codes</strong>, used or unused. Enter your current
          authenticator code to confirm.
        </p>
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-3">{error}</div>}
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={6}
          pattern="\d{6}"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-mono text-center tracking-widest text-lg"
        />
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            disabled={busy}
            onClick={() => onClose(null)}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
            style={{ background: 'var(--color-accent)' }}
          >
            {busy ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </form>
    </div>
  );
}

function NewRecoveryCodesModal({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);

  function downloadCodes() {
    const blob = new Blob(
      [
        `Asset Risk Manager — TOTP recovery codes (regenerated)\n`,
        `Generated: ${new Date().toISOString()}\n\n`,
        `Each code can be used ONCE if you lose access to your authenticator.\n\n`,
        ...codes.map((c) => `${c}\n`),
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asset-risk-manager-recovery-codes-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setAcknowledged(true);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Save your new recovery codes</h3>
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-4">
          Your previous codes are now invalid. These new codes are shown <strong>once</strong>.
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
            {codes.map((c) => (
              <li key={c} className="bg-white rounded px-3 py-1.5 border border-gray-200 text-center">
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={downloadCodes}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Download as text file
          </button>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(codes.join('\n'));
              setAcknowledged(true);
            }}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Copy to clipboard
          </button>
        </div>
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1"
          />
          <span>I've saved these codes somewhere I can find them again.</span>
        </label>
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!acknowledged}
            onClick={onClose}
            className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
            style={{ background: 'var(--color-accent)' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
